import { useEffect, useMemo, useState } from "react";
import { formatPrice, takesOrders } from "../../menu.js";
import { loadOrder, orderNumber, watchOrders } from "../../orders.js";
import { PAYMENT_LABELS, verifyMpPayment } from "../../mercadoPago.js";
import { notifyError } from "./useBusiness";
import Dialog from "./Dialog";
import Icon from "./Icon";

const PAGE = 50;

const DELIVERY = { retiro: "Retiro en el local", envio: "Envío a domicilio" };

const shortDate = (date) => date ? date.toLocaleString("es-AR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "Recién llegado";
const longDate = (date) => date ? date.toLocaleString("es-AR", { weekday: "long", day: "numeric", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "Recién llegado";
// `total` es el de los productos (lo que más le importa al negocio); si el carrito sumó un
// envío (`envio`), se muestra aparte junto con el total con envío.
const totalText = (order) => typeof order.total === "number" ? formatPrice(order.total) : "A confirmar";
const deliveryText = (order) => typeof order.envio === "number" ? (order.envio ? formatPrice(order.envio) : "Gratis") : "A confirmar";
const withDeliveryText = (order) => typeof order.total === "number" && typeof order.envio === "number" ? formatPrice(order.total + order.envio) : "A confirmar";

// Estado del cobro con Mercado Pago (pedido con pago "mp"). `pagoMp` lo escribe n8n.
const paymentOf = (order) => order.pago === "mp" ? (order.pagoMp?.estado || "esperando") : null;

const PaymentBadge = ({ order }) => {
    const estado = paymentOf(order);
    if (!estado) return null;
    return <span className={"lp-panel-pay lp-panel-pay-" + estado}>{PAYMENT_LABELS[estado] || PAYMENT_LABELS.esperando}</span>;
}

// "#000012", "000012" o "12" buscan el pedido 12.
const numberIn = (term) => {
    const match = term.match(/^#?0*(\d+)$/);
    return match ? Number(match[1]) : null;
}

const matches = (order, term) => {
    const wanted = numberIn(term);
    // Con # o ceros adelante se busca ese número justo; si no, cualquiera que lo contenga.
    if (wanted !== null && (/^[#0]/.test(term) ? order.numero === wanted : String(order.numero).includes(String(wanted)))) return true;
    const text = [
        orderNumber(order.numero), shortDate(order.fecha), order.nombre, order.direccion, order.nota,
        DELIVERY[order.entrega], PAYMENT_LABELS[paymentOf(order)], ...(order.items || []).map((i) => i.nombre)
    ].filter(Boolean).join(" ").toLowerCase();
    return text.includes(term);
}

// Pedidos que llegaron desde el menú, del más nuevo al más viejo. Se actualiza solo.
const OrdersList = ({ business, onGoToSettings }) => {

    const [orders, setOrders] = useState(null);
    const [count, setCount] = useState(PAGE);
    const [hasMore, setHasMore] = useState(false);
    const [failed, setFailed] = useState(false);
    const [retry, setRetry] = useState(0);
    const [q, setQ] = useState("");
    const [found, setFound] = useState(null);
    const [view, setView] = useState(null);

    useEffect(() => {
        setFailed(false);
        return watchOrders(business.usuarioEmpresa, count, (list, more) => {
            setOrders(list);
            setHasMore(more);
        }, (error) => {
            console.error(error);
            setFailed(true);
        });
    }, [business.usuarioEmpresa, count, retry]);

    const term = q.trim().toLowerCase();
    const shown = useMemo(() => (orders || []).filter((o) => !term || matches(o, term)), [orders, term]);

    // Un número de pedido que no está entre los cargados se busca directo.
    const wanted = numberIn(term);
    const missing = wanted !== null && orders && !orders.some((o) => o.numero === wanted);
    useEffect(() => {
        setFound(null);
        if (!missing) return;
        let active = true;
        loadOrder(business.usuarioEmpresa, wanted).then((order) => active && setFound(order)).catch(() => {});
        return () => { active = false; };
    }, [business.usuarioEmpresa, wanted, missing]);

    const list = found ? [found, ...shown] : shown;
    const on = takesOrders(business);

  return (
    <section className="lp-panel-card lp-panel-stack lp-panel-orders">
        <div className="lp-panel-card-head">
            <h2>Pedidos</h2>
            {orders && orders.length > 0 && <span className="lp-panel-badge">Último {orderNumber(orders[0].numero)}</span>}
        </div>

        {!on &&
            <p className="lp-panel-notice">
                Los pedidos por WhatsApp están desactivados, así que no te van a llegar pedidos nuevos.{" "}
                <button type="button" className="lp-text-link lp-panel-only-mobile" onClick={onGoToSettings}>Activarlos</button>
            </p>
        }

        <div className="lp-panel-search">
            <Icon name="search" size={18}></Icon>
            <label htmlFor="buscar-pedido" className="lp-panel-sr">Buscar pedidos</label>
            <input id="buscar-pedido" type="search" placeholder="Buscar por número, cliente o producto…" value={q} onChange={(e) => setQ(e.target.value)}/>
        </div>

        {failed ?
            <div className="lp-panel-empty">
                <p>No pudimos cargar los pedidos.</p>
                <button type="button" className="lp-text-link" onClick={() => setRetry((n) => n + 1)}>Reintentar</button>
            </div> :
        !orders ?
            <p className="lp-panel-empty">Cargando pedidos…</p> :
        !orders.length ?
            <p className="lp-panel-empty">Todavía no recibiste pedidos. Cuando un cliente envíe un pedido desde tu menú, lo vas a ver acá con todos los detalles.</p> :
        !list.length ?
            <p className="lp-panel-empty">No encontramos pedidos con "{q}".</p> :
            <ul className="lp-panel-orders-list">
                {list.map((o) => (
                    <li key={o.numero}>
                        <button type="button" onClick={() => setView(o)}>
                            <span className="lp-panel-order-main">
                                <strong>Pedido {orderNumber(o.numero)}</strong>
                                <span className="lp-panel-muted">{shortDate(o.fecha)}</span>
                                <PaymentBadge order={o}></PaymentBadge>
                            </span>
                            <span className="lp-panel-order-total">
                                <strong>{totalText(o)}</strong>
                                {o.entrega === "envio" && typeof o.envio === "number" && o.envio > 0 && <span className="lp-panel-muted">+ {formatPrice(o.envio)} envío</span>}
                            </span>
                            <Icon name="arrow" size={18}></Icon>
                        </button>
                    </li>
                ))}
            </ul>
        }

        {!failed && hasMore && !term &&
            <button type="button" className="lp-btn lp-btn-secondary lp-panel-btn-sm" onClick={() => setCount((n) => n + PAGE)}>Ver pedidos anteriores</button>
        }

        <Dialog open={!!view} title={view ? "Pedido " + orderNumber(view.numero) : ""} onClose={() => setView(null)}>
            {view && <OrderDetail order={(orders || []).find((o) => o.numero === view.numero) || view} usuarioEmpresa={business.usuarioEmpresa}></OrderDetail>}
        </Dialog>
    </section>
  )
}

const OrderDetail = ({ order, usuarioEmpresa }) => {

    const estado = paymentOf(order);
    const [checking, setChecking] = useState(false);
    const [checked, setChecked] = useState(null);

    // Le pregunta a Mercado Pago por el pago; si cambió, n8n lo guarda y la lista se actualiza sola.
    const check = async () => {
        setChecking(true);
        try {
            const result = await verifyMpPayment(usuarioEmpresa, order.numero);
            setChecked(result.estado === "sin-pago" ? "Mercado Pago todavía no tiene un pago para este pedido." :
                result.estado === "sin-cuenta" ? "Tu cuenta de Mercado Pago no está conectada." : null);
        } catch (error) {
            console.error(error);
            notifyError("No pudimos revisar el pago. Probá de nuevo en unos minutos.");
        }
        setChecking(false);
    }

    const items = order.items || [];
    const units = items.reduce((n, i) => n + (Number(i.cantidad) || 0), 0);
    const rows = [
        ["Fecha", longDate(order.fecha)],
        ["Entrega", DELIVERY[order.entrega] || "Sin indicar"],
        ["Pago", estado ? <PaymentBadge order={order}></PaymentBadge> : "A coordinar por WhatsApp"],
        ["Cliente", order.nombre],
        ["Dirección", order.direccion],
        ["Aclaraciones", order.nota]
    ].filter(([, value]) => value);

  return (
    <div className="lp-panel-stack lp-panel-order">
        <dl className="lp-panel-rows">
            {rows.map(([label, value]) => (
                <div key={label}>
                    <dt>{label}</dt>
                    <dd>{value}</dd>
                </div>
            ))}
        </dl>

        {estado && estado !== "pagado" &&
            <div className="lp-panel-stack">
                <p className="lp-panel-muted">
                    {estado === "esperando" ? "El cliente eligió pagar con Mercado Pago y todavía no se registró el pago." :
                        estado === "pendiente" ? "Mercado Pago está procesando el pago." : "El pago con Mercado Pago no se completó: coordiná el cobro con el cliente."}
                    {checked && " " + checked}
                </p>
                <button type="button" className="lp-btn lp-btn-secondary lp-panel-btn-sm lp-panel-left" disabled={checking} onClick={check}>
                    <Icon name="refresh" size={16}></Icon> {checking ? "Revisando…" : "Revisar el pago"}
                </button>
            </div>
        }
        {estado === "pagado" && typeof order.pagoMp?.monto === "number" &&
            <p className="lp-panel-muted">Cobrado con Mercado Pago: {formatPrice(order.pagoMp.monto)}{order.pagoMp.id ? ` (operación ${order.pagoMp.id})` : ""}.</p>
        }

        <div>
            <span className="lp-panel-label">Productos ({units})</span>
            <ul className="lp-panel-order-items">
                {items.map((i, index) => (
                    <li key={i.id + index}>
                        <span className="lp-panel-order-qty">{i.cantidad} x</span>
                        <span className="lp-panel-order-name">
                            {i.nombre}
                            {i.precio !== null && <span className="lp-panel-muted">{formatPrice(i.precio)} c/u</span>}
                        </span>
                        <strong>{typeof i.subtotal === "number" ? formatPrice(i.subtotal) : ""}</strong>
                    </li>
                ))}
            </ul>
        </div>

        <div className="lp-panel-order-sum">
            <span>{order.entrega === "envio" ? "Total productos" : "Total"}</span>
            <strong>{totalText(order)}</strong>
        </div>
        {order.entrega === "envio" &&
            <dl className="lp-panel-order-ship">
                <div>
                    <dt>Envío</dt>
                    <dd>{deliveryText(order)}</dd>
                </div>
                <div>
                    <dt>Total con envío</dt>
                    <dd>{withDeliveryText(order)}</dd>
                </div>
            </dl>
        }
    </div>
  )
}

export default OrdersList
